"use client"

import Aside from "@modules/global-shell/components/aside"
import { useGlobalAside } from "@modules/global-shell/context/global-aside-context"
import { Adjustments, ChevronDownMini, ChevronLeftMini, XMark } from "@medusajs/icons"
import type { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import styles from "./collection-refinement-bar.module.css"

const SORT_OPTIONS: Array<{ value: SortOptions; label: string }> = [
  { value: "created_at", label: "En yeniler" },
  { value: "price_asc", label: "Fiyat: düşükten yükseğe" },
  { value: "price_desc", label: "Fiyat: yüksekten düşüğe" },
]

type StockValue = "all" | "true" | "false"
type Panel = "root" | "stock" | "price" | "sort"
type Dropdown = "stock" | "price" | "sort" | null

const normalizePrice = (value: string) => value.replace(",", ".").replace(/[^0-9.]/g, "")

export default function CollectionRefinementBar({
  totalCount,
  sortBy,
}: {
  totalCount: number
  sortBy: SortOptions
}) {
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const globalAside = useGlobalAside()
  const barRef = useRef<HTMLDivElement>(null)
  const [dropdown, setDropdown] = useState<Dropdown>(null)
  const [panel, setPanel] = useState<Panel>("root")

  const stockParam = searchParams.get("inStock")
  const currentStock: StockValue = stockParam === "true" || stockParam === "false" ? stockParam : "all"
  const currentMin = searchParams.get("min") ?? ""
  const currentMax = searchParams.get("max") ?? ""
  const [stockDraft, setStockDraft] = useState<StockValue>(currentStock)
  const [minDraft, setMinDraft] = useState(currentMin)
  const [maxDraft, setMaxDraft] = useState(currentMax)
  const [sortDraft, setSortDraft] = useState<SortOptions>(sortBy)
  const filterOpen = globalAside.isOpen("filter")

  useEffect(() => {
    setStockDraft(currentStock)
    setMinDraft(currentMin)
    setMaxDraft(currentMax)
    setSortDraft(sortBy)
  }, [currentMax, currentMin, currentStock, sortBy])

  useEffect(() => {
    if (!filterOpen) return
    setPanel("root")
    setStockDraft(currentStock)
    setMinDraft(currentMin)
    setMaxDraft(currentMax)
    setSortDraft(sortBy)
  }, [currentMax, currentMin, currentStock, filterOpen, sortBy])

  useEffect(() => {
    const close = (event: MouseEvent | TouchEvent) => {
      if (!barRef.current?.contains(event.target as Node)) setDropdown(null)
    }
    document.addEventListener("mousedown", close)
    document.addEventListener("touchstart", close)
    return () => {
      document.removeEventListener("mousedown", close)
      document.removeEventListener("touchstart", close)
    }
  }, [])

  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((option) => option.value === sortBy)?.label ?? "En yeniler",
    [sortBy]
  )
  const activeFilterCount = (currentStock !== "all" ? 1 : 0) + (currentMin || currentMax ? 1 : 0)

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams.toString())
    Object.entries(patch).forEach(([key, value]) => {
      if (!value) next.delete(key)
      else next.set(key, value)
    })
    next.delete("page")
    const query = next.toString()
    router.push(query ? `${pathname}?${query}` : pathname)
  }

  const applyStock = (closeAside = false) => {
    updateParams({ inStock: stockDraft === "all" ? undefined : stockDraft })
    setDropdown(null)
    if (closeAside) setPanel("root")
  }

  const applyPrice = (closeAside = false) => {
    const min = normalizePrice(minDraft)
    const max = normalizePrice(maxDraft)
    const minValue = min ? Number(min) : undefined
    const maxValue = max ? Number(max) : undefined
    if (minValue != null && maxValue != null && minValue > maxValue) {
      updateParams({ min: String(maxValue), max: String(minValue) })
    } else {
      updateParams({ min: min || undefined, max: max || undefined })
    }
    setDropdown(null)
    if (closeAside) setPanel("root")
  }

  const applySort = (value: SortOptions, closeAside = false) => {
    setSortDraft(value)
    updateParams({ sortBy: value })
    setDropdown(null)
    if (closeAside) setPanel("root")
  }

  const clearAll = () => {
    setStockDraft("all")
    setMinDraft("")
    setMaxDraft("")
    setSortDraft("created_at")
    updateParams({ inStock: undefined, min: undefined, max: undefined, sortBy: "created_at" })
  }

  const applyAll = () => {
    const min = normalizePrice(minDraft)
    const max = normalizePrice(maxDraft)
    const minValue = min ? Number(min) : undefined
    const maxValue = max ? Number(max) : undefined
    const pricePatch = minValue != null && maxValue != null && minValue > maxValue
      ? { min: String(maxValue), max: String(minValue) }
      : { min: min || undefined, max: max || undefined }
    updateParams({
      inStock: stockDraft === "all" ? undefined : stockDraft,
      ...pricePatch,
      sortBy: sortDraft,
    })
    globalAside.close("filter")
  }

  const openMobileFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDropdown(null)
    globalAside.open("filter", event.currentTarget)
  }

  const renderStockOptions = () => (
    <div className={styles.choiceList}>
      {[
        ["all", "Tüm ürünler"],
        ["true", "Stokta olanlar"],
        ["false", "Tükendi"],
      ].map(([value, label]) => (
        <label key={value} className={styles.choiceRow}>
          <input
            type="radio"
            name="collection-stock"
            checked={stockDraft === value}
            onChange={() => setStockDraft(value as StockValue)}
          />
          <span>{label}</span>
        </label>
      ))}
    </div>
  )

  const renderPriceInputs = () => (
    <div className={styles.priceInputs}>
      <label className={styles.priceField}>
        <span>₺</span>
        <input
          inputMode="decimal"
          aria-label="Minimum fiyat"
          placeholder="En az"
          value={minDraft}
          onChange={(event) => setMinDraft(normalizePrice(event.target.value))}
        />
      </label>
      <span className={styles.priceDash}>–</span>
      <label className={styles.priceField}>
        <span>₺</span>
        <input
          inputMode="decimal"
          aria-label="Maksimum fiyat"
          placeholder="En çok"
          value={maxDraft}
          onChange={(event) => setMaxDraft(normalizePrice(event.target.value))}
        />
      </label>
    </div>
  )

  const renderSortOptions = (mobile = false) => (
    <div className={styles.sortList}>
      {SORT_OPTIONS.map((option) => (
        <button
          type="button"
          key={option.value}
          className={styles.sortOption}
          data-active={sortDraft === option.value ? "true" : undefined}
          onClick={() => mobile ? setSortDraft(option.value) : applySort(option.value)}
        >
          <span>{option.label}</span>
          {sortDraft === option.value ? <span aria-hidden="true">✓</span> : null}
        </button>
      ))}
    </div>
  )

  const dropdownFooter = (onReset: () => void, onApply: () => void) => (
    <div className={styles.dropdownFooter}>
      <button type="button" className={styles.secondaryButton} onClick={onReset}>Temizle</button>
      <button type="button" className={styles.primaryButton} onClick={onApply}>Uygula</button>
    </div>
  )

  return (
    <>
      <div className={styles.filterSurface} ref={barRef} data-testid="collection-filter-bar">
        <div className={styles.desktopBar}>
          <div className={styles.filterGroup}>
            <span className={styles.label}>Filtrele:</span>

            <div className={styles.dropdownWrap}>
              <button type="button" className={styles.trigger} aria-expanded={dropdown === "stock"} onClick={() => setDropdown(dropdown === "stock" ? null : "stock")}>
                Stok durumu <ChevronDownMini aria-hidden="true" />
              </button>
              {dropdown === "stock" ? (
                <div className={styles.dropdown} data-testid="stock-filter-menu">
                  <div className={styles.dropdownHeader}><strong>Stok durumu</strong></div>
                  <div className={styles.dropdownBody}>{renderStockOptions()}</div>
                  {dropdownFooter(() => setStockDraft("all"), () => applyStock())}
                </div>
              ) : null}
            </div>

            <div className={styles.dropdownWrap}>
              <button type="button" className={styles.trigger} aria-expanded={dropdown === "price"} onClick={() => setDropdown(dropdown === "price" ? null : "price")}>
                Fiyat {currentMin || currentMax ? <span className={styles.countBadge}>1</span> : null} <ChevronDownMini aria-hidden="true" />
              </button>
              {dropdown === "price" ? (
                <div className={styles.dropdown} data-testid="price-filter-menu">
                  <div className={styles.dropdownHeader}><strong>Fiyat aralığı</strong></div>
                  <div className={styles.dropdownBody}>{renderPriceInputs()}</div>
                  {dropdownFooter(() => { setMinDraft(""); setMaxDraft("") }, () => applyPrice())}
                </div>
              ) : null}
            </div>
          </div>

          <div className={styles.sortGroup}>
            <div className={styles.dropdownWrap}>
              <button type="button" className={styles.trigger} aria-expanded={dropdown === "sort"} onClick={() => setDropdown(dropdown === "sort" ? null : "sort")}>
                <span>Sırala: <strong>{sortLabel}</strong></span> <ChevronDownMini aria-hidden="true" />
              </button>
              {dropdown === "sort" ? <div className={`${styles.dropdown} ${styles.sortDropdown}`}>{renderSortOptions()}</div> : null}
            </div>
            <span className={styles.productCount}>{totalCount} ürün</span>
          </div>
        </div>

        <div className={styles.mobileBar}>
          <button type="button" className={styles.mobileTrigger} onClick={openMobileFilter}>
            <Adjustments aria-hidden="true" />
            <span>Filtrele ve sırala</span>
            {activeFilterCount ? <span className={styles.mobileCount}>{activeFilterCount}</span> : null}
          </button>
          <span className={styles.productCount}>{totalCount} ürün</span>
        </div>
      </div>

      <Aside kind="filter" label="Filtrele ve sırala">
        <div className={styles.asideShell} data-testid="collection-filter-aside">
          <header className={styles.asideHeader}>
            {panel !== "root" ? (
              <button type="button" className={styles.iconButton} onClick={() => setPanel("root")} aria-label="Filtrelere dön">
                <ChevronLeftMini aria-hidden="true" />
              </button>
            ) : <span className={styles.iconSpacer} />}
            <strong>{panel === "root" ? "Filtrele ve sırala" : panel === "stock" ? "Stok durumu" : panel === "price" ? "Fiyat" : "Sırala"}</strong>
            <button type="button" className={styles.iconButton} onClick={() => globalAside.close("filter")} aria-label="Filtreleri kapat">
              <XMark aria-hidden="true" />
            </button>
          </header>

          {panel === "root" ? (
            <div className={styles.asideRoot}>
              <p className={styles.asideCount}>{totalCount} ürün gösteriliyor</p>
              <div className={styles.asideSections}>
                <button type="button" onClick={() => setPanel("stock")}><span>Stok durumu</span><span>{stockDraft === "true" ? "Stokta" : stockDraft === "false" ? "Tükendi" : "Tümü"} ›</span></button>
                <button type="button" onClick={() => setPanel("price")}><span>Fiyat</span><span>{minDraft || maxDraft ? `${minDraft || "0"} – ${maxDraft || "∞"} ₺` : "Tümü"} ›</span></button>
                <button type="button" onClick={() => setPanel("sort")}><span>Sırala</span><span>{SORT_OPTIONS.find((option) => option.value === sortDraft)?.label} ›</span></button>
              </div>
            </div>
          ) : (
            <div className={styles.asidePanel}>
              {panel === "stock" ? renderStockOptions() : panel === "price" ? renderPriceInputs() : renderSortOptions(true)}
              <div className={styles.panelActions}>
                <button type="button" className={styles.secondaryButton} onClick={() => {
                  if (panel === "stock") setStockDraft("all")
                  if (panel === "price") { setMinDraft(""); setMaxDraft("") }
                  if (panel === "sort") setSortDraft("created_at")
                }}>Temizle</button>
                <button type="button" className={styles.primaryButton} onClick={() => {
                  if (panel === "stock") applyStock(true)
                  if (panel === "price") applyPrice(true)
                  if (panel === "sort") applySort(sortDraft, true)
                }}>Uygula</button>
              </div>
            </div>
          )}

          {panel === "root" ? (
            <footer className={styles.asideFooter}>
              <button type="button" className={styles.secondaryButton} onClick={clearAll}>Tümünü temizle</button>
              <button type="button" className={styles.primaryButton} onClick={applyAll}>Sonuçları göster</button>
            </footer>
          ) : null}
        </div>
      </Aside>
    </>
  )
}
